/**
 * SectionShell — common wrapper for landing page sections.
 *
 * Renders the <section> element with background styles, padding, radius, shadow,
 * top/bottom dividers, background image overlay, and the inner content container
 * with max-width, horizontal padding, and scroll-reveal animation classes.
 *
 * Usage:
 *   const { s, ref, visible, anim, config, content } = useSectionSetup(block, sharedProps);
 *
 *   return (
 *       <SectionShell s={s} config={config} anim={anim} ref={ref} mediaFolders={mediaFolders}>
 *           <SectionHeader ... />
 *           { ... section content ... }
 *       </SectionShell>
 *   );
 *
 * Props:
 *   s            — object from sectionStyles() (required)
 *   config       — block config object (default {})
 *   anim         — animation className string from getAnimation()
 *   ref          — forwarded ref from useScrollReveal, attached to the inner content div
 *   children     — section content
 *   className    — extra classes on the <section> element
 *   innerClass   — extra classes on the inner content wrapper (e.g. 'text-center')
 *   style        — extra inline styles on the <section> element
 *   noPad        — if true, omits the vertical padding class
 *   mediaFolders — passed to SectionBgImage for resolving background images
 */
import React, { forwardRef } from 'react';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';

const SectionShell = forwardRef(({
    s,
    config = {},
    anim,
    children,
    className = '',
    innerClass = '',
    style = {},
    noPad = false,
    mediaFolders = {},
}, ref) => {
    const cls = [
        noPad ? '' : s.padClass,
        'relative overflow-hidden',
        s.radiusClass || '',
        s.shadowClass || '',
        className,
    ].filter(Boolean).join(' ');

    const bgS = s.bgStyle?.background
        ? { ...s.bgStyle, ...style }
        : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle, ...style };

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top" config={config} />
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div
                ref={ref}
                className={`relative z-10 ${s.maxWidth || 'max-w-7xl'} mx-auto px-4 sm:px-6 lg:px-8 ${anim || ''} ${innerClass}`.trim()}
            >
                {children}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config} />
        </section>
    );
});

SectionShell.displayName = 'SectionShell';
export default SectionShell;
